import React, { useEffect, useState } from "react"
import ReviewOverview from "./ReviewOverview"
import { Link } from "react-router-dom"
import { Container } from "reactstrap"
import { fetchDashboard } from "./api"

const number = value => Number(value).toLocaleString("en-GB", {
  maximumFractionDigits: 1,
})

const periodDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
})

const dateValue = value => new Date(`${value}T00:00:00Z`)

const periodDate = value => {
  if (!value) return "Unavailable"
  const date = dateValue(value)
  return Number.isNaN(date.getTime()) ? "Unavailable" : periodDateFormatter.format(date)
}

const periodRange = range => {
  if (!range?.startDate || !range?.endDate) return "Unavailable"
  return `${periodDate(range.startDate)} – ${periodDate(range.endDate)}`
}

const periodDays = range => {
  if (!range?.startDate || !range?.endDate) return null
  const start = dateValue(range.startDate)
  const end = dateValue(range.endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null
  return Math.round((end - start) / 86400000) + 1
}

const periodSummary = period => {
  const currentDays = periodDays(period?.ranges?.current)
  const comparisonDays = periodDays(period?.ranges?.comparison)
  if (!currentDays || !comparisonDays) return "Current period versus prior period."

  if (period?.filters?.comparison === "prior-year") {
    return `Last ${currentDays} days versus the same period last year.`
  }

  return `Last ${currentDays} days versus the immediately preceding ${comparisonDays} days.`
}

const days = value => `${number(value)} ${Math.abs(value) === 1 ? "day" : "days"}`

const unitValue = (unit, value) => {
  if (unit === "percentage" || unit === "percent") return `${number(value)}%`
  if (unit === "days") return days(value)
  if (unit === "events-per-event-participant") {
    return `${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`
  }
  if (unit === "participations-per-player" || unit === "participations-per-event-participant") {
    return `${number(value)}×`
  }
  return number(value)
}

const metricValue = metric => {
  const value = metric?.current?.value
  if (value === null || value === undefined) return "Unavailable"
  return unitValue(metric.unit, value)
}

const isPresent = value => value !== null && value !== undefined

// The churn cards compare whole 12-month cycles, so they show the previous cycle's value.
const CYCLE_METRICS = new Set(["playerRetention", "playerChurn", "eventPlayerChurn"])

const comparisonText = metric => {
  const comparison = metric?.comparison?.value
  if (!isPresent(comparison)) {
    return `${CYCLE_METRICS.has(metric?.key) ? "Previous cycle" : "Previous"}: Unavailable`
  }
  const delta = metric?.delta?.percentage
  if (isPresent(delta)) return `${delta > 0 ? "+" : ""}${number(delta)}% vs prior period`
  const points = metric?.delta?.points
  if (isPresent(points) && !CYCLE_METRICS.has(metric.key)) {
    return `${points > 0 ? "+" : ""}${number(points)} pp vs prior period`
  }
  // The times to a first event or activity change in days.
  const dayChange = metric?.delta?.days
  if (isPresent(dayChange)) return `${dayChange > 0 ? "+" : ""}${days(dayChange)} vs prior period`
  return `${CYCLE_METRICS.has(metric.key) ? "Previous cycle" : "Previous"}: ${unitValue(metric.unit, comparison)}`
}

// Good news is green and bad news red; an unknown or zero change stays neutral. A lower-is-better
// metric (withdrawals, churn, unconfirmed sign-ups, the times to a first activity) improves when it falls.
const deltaClass = metric => {
  const change = metric?.delta?.percentage ?? metric?.delta?.points ?? metric?.delta?.days
  if (!Number.isFinite(change) || change === 0) return ""
  const improved = metric?.polarity === "lower-is-better" ? change < 0 : change > 0
  return improved ? "is-positive" : "is-negative"
}

const MetricCard = ({ metric, children }) => (
  <article className="project-metric-card">
    <span>{metric.label}</span>
    <strong>{metricValue(metric)}</strong>
    <small className={deltaClass(metric)}>
      {comparisonText(metric)}
    </small>
    {children}
  </article>
)

// What each New accounts card says about its current period, as Extended Analytics does; nothing
// beside an Unavailable value.
const signupShare = side => {
  if (Number.isFinite(side?.share)) return `${number(side.share)}% of ${number(side.signups)} sign-ups`
  return side?.signups === 0 ? "No player sign-ups in this period" : null
}

const activatedAccounts = side => {
  if (Number.isFinite(side?.value)) return `${number(side.numerator)} of ${number(side.denominator)} new accounts`
  return side?.denominator === 0 ? "No new accounts in this period" : null
}

const daysFromStarts = (fromSignup, fromConfirmation) => (
  Number.isFinite(fromSignup) ? `${days(fromSignup)} from sign-up · ${days(fromConfirmation)} from confirmation` : null
)

const medianDays = side => {
  const line = daysFromStarts(side?.value, side?.fromConfirmation)
  return line && `Median ${line}`
}

const NEW_ACCOUNT_LINES = {
  unconfirmedSignups: signupShare,
  eventActivation: activatedAccounts,
  activityActivation: activatedAccounts,
  timeToFirstEvent: medianDays,
  timeToFirstActivity: medianDays,
}

// The median is on the card; the averages, which one long wait can pull far from it, in the details.
const NewAccountLines = ({ metric }) => {
  const line = NEW_ACCOUNT_LINES[metric.key]?.(metric.current)
  const averages = metric.unit === "days"
    ? daysFromStarts(metric.current?.average, metric.current?.averageFromConfirmation)
    : null
  return (
    <>
      {line && <small>{line}</small>}
      {averages && (
        <details className="project-metric-details">
          {/* Both time cards have one: the name says whose averages they are. */}
          <summary aria-label={`Averages for ${metric.label}`}>Averages</summary>
          <small>Average {averages}</small>
        </details>
      )}
    </>
  )
}

const NewAccountsRow = ({ metrics }) => {
  if (!Array.isArray(metrics) || !metrics.length) return null
  return (
    <section className="project-pulse-row" aria-labelledby="new-accounts-heading">
      <div className="project-section-heading">
        <div>
          <h3 id="new-accounts-heading">New accounts</h3>
          <p>Player sign-ups in the period: the accounts confirmed, those still awaiting email confirmation, and how soon the new accounts played.</p>
        </div>
      </div>
      <div className="project-pulse-grid">
        {metrics.map(metric => (
          <MetricCard metric={metric} key={metric.key}>
            <NewAccountLines metric={metric} />
          </MetricCard>
        ))}
      </div>
    </section>
  )
}

const churnCalculation = metric => {
  const churnedPlayers = metric.current?.churnedPlayers
  const cohortPlayers = metric.current?.denominatorValue
  if (!isPresent(churnedPlayers) || !isPresent(cohortPlayers)) {
    return "Non-returning cohort players ÷ players in the cohort window × 100"
  }
  if (cohortPlayers === 0) return "No players in the cohort window."
  return `${number(churnedPlayers)} non-returning players ÷ ${number(cohortPlayers)} cohort players × 100`
}

const ChurnCard = ({ scope, metric, eligibility }) => (
  <article className="project-metric-card project-churn-card">
    <p className="project-dashboard-eyebrow">{scope}</p>
    <span>{metric.label}</span>
    <strong>{metricValue(metric)}</strong>
    <small className={deltaClass(metric)}>
      {comparisonText(metric)}
    </small>
    <div className="project-churn-calculation">
      <b>{churnCalculation(metric)}</b>
      <p>{eligibility}</p>
    </div>
  </article>
)

const ChurnPanel = ({ churn }) => {
  const cards = [
    ...(churn?.metric ? [{
      key: "global",
      scope: "Global",
      metric: churn.metric,
      eligibility: "Confirmed event participation, completed friendly matches and elapsed confirmed bookings, in any sport.",
    }] : []),
    ...(churn?.eventMetrics || []).filter(item => item?.metric).map(item => ({
      key: item.sport,
      scope: item.label,
      metric: item.metric,
      eligibility: `Confirmed event participation in ${item.sport} events only.`,
    })),
  ]
  if (!cards.length) return null

  return (
    <section className="project-retention-section" aria-label="Rolling 12-month player churn">
      <div className="project-section-heading">
        <div>
          <p className="project-dashboard-eyebrow">Annual player health</p>
          <h2>Rolling 12-month player churn</h2>
          <p>Share of players from the preceding 12-month cohort who were not active again in the current 12 months. Each player is counted once per window.</p>
        </div>
        <dl className="project-pulse-periods" aria-label="Player churn calculation periods">
          <div>
            <dt>Current activity window</dt>
            <dd>{periodRange(churn.period?.ranges?.current)}</dd>
          </div>
          <div>
            <dt>Cohort window</dt>
            <dd>{periodRange(churn.period?.ranges?.comparison)}</dd>
          </div>
        </dl>
      </div>
      <div className="project-churn-grid">
        {cards.map(({ key, ...card }) => <ChurnCard key={key} {...card} />)}
      </div>
    </section>
  )
}

const EventPaceRow = ({ event }) => (
  <article className="registration-pace-row">
    <div className="registration-pace-event">
      <Link to={`/events/${event.id}`}>{event.name}</Link>
      <span>
        {[event.location?.city, event.location?.country].filter(Boolean).join(", ") || "Location unavailable"}
        {" · "}{event.daysUntilRegistrationCloses} day{event.daysUntilRegistrationCloses === 1 ? "" : "s"} left
      </span>
    </div>
    <div className="registration-pace-bar">
      <div
        aria-label={`${event.name} is ${number(event.fillRate || 0)} percent full`}
        aria-valuemax="100"
        aria-valuemin="0"
        aria-valuenow={Math.min(event.fillRate || 0, 100)}
        role="progressbar"
      >
        <i style={{ width: `${Math.min(event.fillRate || 0, 100)}%` }} />
      </div>
      <span>{event.registrations}/{event.capacity || "—"} registrations</span>
    </div>
    <strong>{event.fillRate === null ? "—" : `${number(event.fillRate)}%`}</strong>
  </article>
)

const EventsDepartment = ({ department }) => (
  <section className="project-department-panel project-events-panel" aria-labelledby="events-performance-heading">
    <header>
      <div>
        <p className="project-dashboard-eyebrow">Event operations</p>
        <h2 id="events-performance-heading">Registration-open event performance</h2>
        <p>Every event accepting registrations now, compared with a clearly labelled prior-year benchmark.</p>
      </div>
      <div className="registration-summary">
        <div className="registration-benchmark" aria-label="Main-list participants">
          <span>Main-list participants</span>
          <strong>{number(department.summary.mainListParticipants)}</strong>
          <small>Across registration-open events</small>
        </div>
        <div className="registration-benchmark" aria-label="Registration fill benchmark">
          <span>{department.comparison.label}</span>
          <strong>{department.comparison.medianFillRate === null ? "Unavailable" : `${number(department.comparison.medianFillRate)}%`}</strong>
          <small>
            {department.comparison.changePoints === null
              ? "No comparable cohort"
              : `${department.comparison.changePoints > 0 ? "+" : ""}${number(department.comparison.changePoints)} points now`}
          </small>
        </div>
      </div>
    </header>

    <div className="registration-layout">
      <section className="registration-pace-card" aria-labelledby="registration-pace-heading">
        <div className="project-section-heading">
          <div>
            <h3 id="registration-pace-heading">Registration pace</h3>
            <p>Main-list registrations against configured event capacity.</p>
          </div>
          <Link to="/events-management">All events</Link>
        </div>
        {department.events?.length
          ? department.events.map(event => <EventPaceRow event={event} key={event.id} />)
          : <p className="project-empty-state">No events have registrations open right now.</p>}
      </section>

      <section className="registration-attention-card" aria-labelledby="registration-attention-heading">
        <div className="project-section-heading">
          <div>
            <h3 id="registration-attention-heading">Needs attention</h3>
            <p>Up to five events with the clearest operational signal.</p>
          </div>
        </div>
        {department.needsAttention?.length ? (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Fill</th>
                  <th>Players</th>
                  <th>Signal</th>
                  <th><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody>
                {department.needsAttention.map(event => (
                  <tr key={event.id}>
                    <td><strong>{event.name}</strong></td>
                    <td>{event.fillRate === null ? "—" : `${number(event.fillRate)}%`}</td>
                    <td>{number(event.mainListParticipants)}</td>
                    <td>
                      <div className="registration-signals">
                        {event.reasons.map(reason => <span key={reason}>{reason}</span>)}
                      </div>
                    </td>
                    <td><Link to={`/events/${event.id}`}>Open event</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="project-empty-state">No registration-open events currently require attention.</p>}
      </section>
    </div>
  </section>
)

const LoadingState = () => (
  <div className="project-dashboard-state" role="status">
    <span className="project-dashboard-loader" />
    <span>Loading project overview…</span>
  </div>
)

const Dashboard = () => {
  const [snapshot, setSnapshot] = useState(null)
  const [requestState, setRequestState] = useState("loading")
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let active = true
    setRequestState("loading")
    fetchDashboard()
      .then(response => {
        if (!active) return
        setSnapshot(response.data)
        setRequestState("idle")
      })
      .catch(() => {
        if (!active) return
        setRequestState("error")
      })
    return () => { active = false }
  }, [reloadKey])

  return (
    <div className="page-content project-dashboard-page">
      <Container fluid>
        {requestState === "loading" && <LoadingState />}
        {requestState === "error" && (
          <div className="project-dashboard-state project-dashboard-error" role="alert">
            <strong>Project overview could not be loaded.</strong>
            <span>The dashboard data is temporarily unavailable.</span>
            <button type="button" onClick={() => setReloadKey(value => value + 1)}>Try again</button>
          </div>
        )}
        {snapshot && requestState === "idle" && (
          <>
            <header className="project-dashboard-header">
              <div>
                <p className="project-dashboard-eyebrow">Management dashboard</p>
                <h1>Project Overview Dashboard</h1>
                <p>The few signals every department should understand before opening its detailed tools.</p>
              </div>
              <div className="project-dashboard-freshness">
                <span>Data through <strong>{snapshot.metadata.dataAvailableThrough || "Unavailable"}</strong></span>
                <span>Contract <strong>v{snapshot.contractVersion}</strong></span>
              </div>
            </header>

            <section className="project-pulse-section" aria-label="Company pulse">
              <div className="project-section-heading">
                <div>
                  <h2>Company pulse</h2>
                  <p>{periodSummary(snapshot.period)}</p>
                </div>
                <dl className="project-pulse-periods" aria-label="Company pulse calculation periods">
                  <div>
                    <dt>Current period</dt>
                    <dd>{periodRange(snapshot.period?.ranges?.current)}</dd>
                  </div>
                  <div>
                    <dt>Prior period</dt>
                    <dd>{periodRange(snapshot.period?.ranges?.comparison)}</dd>
                  </div>
                </dl>
              </div>
              <div className="project-pulse-grid">
                {snapshot.companyPulse.map(metric => <MetricCard metric={metric} key={metric.key} />)}
              </div>
              <NewAccountsRow metrics={snapshot.newAccounts} />
            </section>

            <ChurnPanel churn={snapshot.playerChurn} />

            <EventsDepartment department={snapshot.events} />
            <ReviewOverview />
          </>
        )}
      </Container>
    </div>
  )
}

export default Dashboard
